import React from 'react';
import { Virtuoso } from 'react-virtuoso';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CardListItem from './card-list-item';
import Poster from '../poster';
import { OptionItem, EventData } from './card.types';

export type ListItem = Record<string, any>;

export interface Props {
  onEvent: (dataType: OptionItem, eventData: EventData) => void;
  onFetchMore: () => void;
  isLoading: boolean;
  error: Node | string | boolean;
  EmptyImage: React.FC<{ width: string }>;
  actionItems: OptionItem[];
  rows?: ListItem[];
  height?: string | number;
  emptyText?: string;
  emptyTextHelper?: string;
  hideAvatar?: boolean;
}

function CardList({ emptyText = 'No items', EmptyImage, ...props }: Props) {
  const isEmpty = !props.isLoading && !props.rows?.length && !props.error;
  const isNotLastItem = (idx: number, rows?: ListItem[]) => ((idx + 1) !== rows?.length);

  const rowContent = (_index: number, rowItem: ListItem) => {
    const isLastItem = !isNotLastItem(_index, props.rows);

    return (
      <Box pb={isLastItem ? 0.5 : undefined}>
        <CardListItem
          key={rowItem.id}
          id={rowItem.id}
          avatarSrc={rowItem.avatar ?? rowItem.image}
          title={rowItem.user || rowItem.name!}
          subtitle={rowItem.description}
          isFirstItem={_index === 0}
          isLastItem={isLastItem}
          onEvent={props.onEvent}
          actionItems={props.actionItems}
          hideAvatar={props.hideAvatar}
        />
        {props.rows && isNotLastItem(_index, props.rows) && (
          <Divider variant="middle" />
        )}
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      data-testid="card-list"
      style={{
        backgroundColor: 'transparent',
        position: 'relative',
        height: '100%',
      }}
    >
      {props.error && <Typography>{props.error}</Typography>}
      {props.isLoading && (
        <Box display="flex" justifyContent="center">
          <CircularProgress sx={{ mt: 3 }} />
        </Box>
      )}
      {isEmpty && (
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="center"
          padding="48px"
        >
          <Poster Image={EmptyImage} imageWidth="300px" heading={emptyText} description={props.emptyTextHelper} />
        </Box>
      )}
      {!props.error && (
        <Virtuoso
          data={props.rows ?? []}
          itemContent={rowContent}
          totalCount={props.rows?.length}
          endReached={props.onFetchMore}
          style={{
            height: props.height ?? '100%',
            outline: 'none',
            overflowY: 'auto',
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
          }}
        />
      )}
    </Paper>
  );
}

export default React.memo(CardList);
